/**
 * Custom button implementation.
 *
 * Copyright (c) Ircam Amplify 2024-2026
 */
import React from 'react'

interface BtnProps {
  className?: string
  label: string
  dark?: boolean
  large?: boolean
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

interface ButtonProps {
  className?: string
  as?: any
  label: string
  dark?: boolean
  large?: boolean
  disabled?: boolean
  to?: string
  state?: any
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

function Btn({
  className = '',
  label,
  dark = true,
  large = true,
  onClick,
}: BtnProps) {
  const colorClass = dark ? 'button-dark' : 'button-light'
  const sizeClass = large ? 'button-large' : 'button-small'
  return (
    <div
      className={`button py-2 px-4 ${colorClass} text-center ${className}`}
      onClick={onClick}
    >
      <div className={`button-label ${sizeClass}`}>{label}</div>
    </div>
  )
}

/**
 * Custom Button component.
 *
 * To define a Button which opens a link, use `as` and `to` props:
 *    <Button
 *      as={Link}
 *      to="/login"
 *      label="Cancel"
 *      dark={false}
 *      className="w-full my-8 py-2 text-center"
 *      disabled={loading}
 *    />
 *
 * To define a Button which execute a function, use `onClick` prop:
 *    <Button
 *      label="Load more"
 *      dark
 *      className="w-full my-8 py-2 text-center"
 *      onClick={() => execute_a_function() }
 *    />
 */
export default function Button({
  className = '',
  as = null,
  label,
  dark = true,
  large = true,
  disabled = false,
  onClick = undefined,
  ...rest
}: ButtonProps) {
  const Wrapper = as
  const disabledClass = disabled ? 'button-disabled' : ''

  if (Wrapper) {
    return (
      <Wrapper {...rest} className={`${className} ${disabledClass}`}>
        <Btn label={label} dark={dark} large={large} />
      </Wrapper>
    )
  }
  return (
    <Btn
      className={className}
      label={label}
      dark={dark}
      large={large}
      onClick={onClick}
    />
  )
}
